// Дефолтная браузерная реализация MediaCaptureFactory — ЗАМОРОЖЕННЫЙ рецепт Diktum live-capture
// (SPEC §4.8, [[reference_diktum_live_capture_recipe]], ⛔ НЕ менять без e2e+R2).
//
// Канон (цитата рецепта — модуль его НЕ переизобретает):
//   - getUserMedia({audio:true}) с БРАУЗЕРНЫМИ ДЕФОЛТАМИ: AGC/NS/EC `on`. НЕ выключать AGC (даёт
//     тихо), НЕ включать EC/NS вручную на iPhone (переводит мик на band-limited голосовой тракт).
//   - захват через MediaRecorder (НЕ AudioWorklet/raw-PCM); платформенный mime по isTypeSupported:
//     Chrome/Edge `audio/webm;codecs=opus`, iOS Safari `audio/mp4`.
//   - mr.start(timeslice=5000) — 5-сек срез (НЕ 1с: короткий срез ломает таймкоды кластеров webm).
//   - финальный blob = конкатенация чанков в ИСХОДНОМ контейнере, БЕЗ клиентского перекодирования;
//     нормализацию `aresample=async=1` делает СЕРВЕР при сборке/декоде.
//   - resilient-resume: слушаем mr.onerror / track.onended / document.visibilitychange.
//   - закрытие сегмента НЕ вешаем на AudioContext.onstatechange (засыпает на телефоне).
//
// Этот файл — путь, который ловит ТОЛЬКО browser-verify (Playwright на app-ui, integration-gate;
// SPEC §6, граница метода «typecheck ≠ запись работает»). Node-тесты гоняют фейк-фабрику.
/** Кандидаты контейнера в порядке предпочтения рецепта Diktum (livePickMime). */
const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/mp4;codecs=mp4a.40.2'];
/**
 * Платформенный выбор контейнера (рецепт Diktum). Chrome/Edge отдадут webm/opus, iOS Safari —
 * mp4. Если ничего не поддержано (теоретически) — пустая строка ⇒ рекордер сообщит 'mic-unavailable'
 * на этапе создания записи (контейнер обязателен для GigaAM, инвариант 4).
 */
export function pickSupportedMime() {
    const MR = globalThis.MediaRecorder;
    if (!MR || typeof MR.isTypeSupported !== 'function')
        return '';
    for (const m of MIME_CANDIDATES) {
        if (MR.isTypeSupported(m))
            return m;
    }
    return '';
}
/** Браузерная фабрика захвата (рецепт Diktum §4.8). */
export function createBrowserCapture() {
    return {
        async getUserMedia() {
            // Браузерные ДЕФОЛТЫ: {audio:true} без ручной правки AGC/NS/EC (рецепт Diktum).
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            return wrapStream(stream);
        },
        pickMime() {
            return pickSupportedMime();
        },
        createRecorder(stream, mime) {
            const raw = stream.raw;
            const mr = mime ? new MediaRecorder(raw, { mimeType: mime }) : new MediaRecorder(raw);
            return wrapRecorder(mr);
        },
        createAnalyser(stream) {
            return createBrowserAnalyser(stream.raw);
        },
    };
}
function wrapStream(raw) {
    let released = false;
    return {
        raw,
        release() {
            if (released)
                return;
            released = true;
            for (const track of raw.getTracks())
                track.stop();
        },
    };
}
function wrapRecorder(mr) {
    const chunkListeners = [];
    const stopListeners = [];
    const errorListeners = [];
    const trackEndedListeners = [];
    mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
            for (const l of chunkListeners)
                l({ data: e.data });
        }
    };
    mr.onstop = () => {
        for (const l of stopListeners)
            l();
    };
    mr.onerror = (e) => {
        for (const l of errorListeners)
            l(e);
    };
    // track.onended — потеря аудио-трека (рецепт Diktum resilient-resume).
    for (const track of mr.stream.getAudioTracks()) {
        track.onended = () => {
            for (const l of trackEndedListeners)
                l();
        };
    }
    // visibilitychange как сигнал прерывания (звонок/блокировка экрана/сворачивание) — рецепт Diktum.
    const onVisibility = () => {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            for (const l of trackEndedListeners)
                l();
        }
    };
    if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', onVisibility);
    }
    let disposed = false;
    return {
        get mimeType() {
            return mr.mimeType;
        },
        start(timesliceMs) {
            mr.start(timesliceMs); // рецепт Diktum: timeslice 5000 (передаёт рекордер)
        },
        stop() {
            if (mr.state !== 'inactive')
                mr.stop();
        },
        onChunk(l) {
            chunkListeners.push(l);
        },
        onStop(l) {
            stopListeners.push(l);
        },
        onError(l) {
            errorListeners.push(l);
        },
        onTrackEnded(l) {
            trackEndedListeners.push(l);
        },
        dispose() {
            if (disposed)
                return;
            disposed = true;
            if (typeof document !== 'undefined')
                document.removeEventListener('visibilitychange', onVisibility);
            try {
                if (mr.state !== 'inactive')
                    mr.stop();
            }
            catch {
                // запись могла уже остановиться — освобождение идемпотентно
            }
        },
    };
}
/**
 * Анализ-метр уровня поверх AudioContext (§4.3). ВАЖНО (рецепт Diktum): жизненный цикл записи НЕ
 * привязан к этому графу — закрытие сегмента не вешается на AudioContext.onstatechange (он засыпает
 * на телефоне). Метр только читает уровни; при сбое — рекордер деградирует к плоскому waveform.
 */
function createBrowserAnalyser(raw) {
    const Ctx = globalThis
        .AudioContext ??
        globalThis.webkitAudioContext;
    if (!Ctx) {
        // Нет AudioContext → метр недоступен: рекордер сам сделает плоский плейсхолдер (§4.3).
        return { level: () => 0, dispose: () => { } };
    }
    const ctx = new Ctx();
    const source = ctx.createMediaStreamSource(raw);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    source.connect(analyser);
    const buf = new Uint8Array(analyser.fftSize);
    let disposed = false;
    return {
        level() {
            if (disposed)
                return 0;
            analyser.getByteTimeDomainData(buf);
            // RMS вокруг центра 128 → 0..1.
            let sumSq = 0;
            for (let i = 0; i < buf.length; i++) {
                const v = ((buf[i] ?? 128) - 128) / 128;
                sumSq += v * v;
            }
            return Math.sqrt(sumSq / buf.length);
        },
        dispose() {
            if (disposed)
                return;
            disposed = true;
            try {
                source.disconnect();
            }
            catch {
                // граф мог уже распасться
            }
            void ctx.close();
        },
    };
}
//# sourceMappingURL=capture.js.map